import { useEffect } from 'react'

interface SeoOptions {
  title: string
  description: string
  keywords?: string[]
  /** JSON-LD structured data objects injected into the document head. */
  schema?: Record<string, unknown>[]
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

/** Sets per-page title, meta tags, and JSON-LD schema markup. */
export function useSeo({ title, description, keywords, schema }: SeoOptions) {
  useEffect(() => {
    document.title = title
    upsertMeta('name', 'description', description)
    if (keywords?.length) upsertMeta('name', 'keywords', keywords.join(', '))
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:description', description)
    upsertMeta('property', 'og:type', 'website')

    const scripts: HTMLScriptElement[] = (schema ?? []).map((entry) => {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.dataset.seo = 'true'
      script.textContent = JSON.stringify(entry)
      document.head.appendChild(script)
      return script
    })

    return () => {
      scripts.forEach((script) => script.remove())
    }
  }, [title, description, keywords, schema])
}
